import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import { cn } from "@/lib/utils";

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root> {
  variant?: "default" | "circle";
  size?: "default" | "md" | "lg";
  indeterminate?: boolean;
  // New props for built-in indeterminate functionality
  children?: React.ReactNode;
  showIndeterminate?: boolean;
  onIndeterminateChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      variant = "default",
      size = "default",
      indeterminate = false,
      checked,
      defaultChecked,
      children,
      showIndeterminate = false,
      onIndeterminateChange,
      ...props
    },
    ref
  ) => {
    const checkboxRef = React.useRef<HTMLButtonElement>(null);
    const [internalChecked, setInternalChecked] = React.useState(
      defaultChecked ?? false
    );

    // Determine the actual checked state
    const isChecked = checked !== undefined ? checked : internalChecked;

    // Handle indeterminate state
    React.useEffect(() => {
      if (checkboxRef.current) {
        // Set the native indeterminate property
        (checkboxRef.current as any).indeterminate = indeterminate;
      }
    }, [indeterminate]);

    // Combine refs using useImperativeHandle for better ref handling
    React.useImperativeHandle(ref, () => checkboxRef.current!, []);

    const getVariantClasses = () => {
      switch (variant) {
        case "circle":
          return "wl-checkbox-circle";
        default:
          return "";
      }
    };

    const getSizeClasses = () => {
      switch (size) {
        case "md":
          return "wl-checkbox-md";
        case "lg":
          return "wl-checkbox-lg";
        default:
          return "";
      }
    };

    return (
      <CheckboxPrimitive.Root
        ref={checkboxRef}
        data-slot="checkbox"
        data-indeterminate={indeterminate}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={(value) => {
          setInternalChecked(value as boolean);
          props.onCheckedChange?.(value);
        }}
        className={cn(
          "wl-input-checkbox",
          getVariantClasses(),
          getSizeClasses(),
          className
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="wl-checkbox-indicator"
        />
      </CheckboxPrimitive.Root>
    );
  }
);

Checkbox.displayName = "Checkbox";

// Enhanced CheckboxGroup component for built-in indeterminate functionality
export interface CheckboxGroupProps {
  items: Array<{
    id: string;
    label: string;
    checked?: boolean;
    disabled?: boolean;
  }>;
  onSelectionChange?: (selectedIds: string[]) => void;
  className?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  variant?: "default" | "circle";
  size?: "default" | "md" | "lg";
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  items,
  onSelectionChange,
  className,
  showSelectAll = true,
  selectAllLabel = "Select All",
  variant = "default",
  size = "default",
}) => {
  const [selectedItems, setSelectedItems] = React.useState<Set<string>>(() => {
    // Initialize with items that are checked, but only on first render
    return new Set(items.filter((item) => item.checked).map((item) => item.id));
  });

  const allChecked = selectedItems.size === items.length && items.length > 0;
  const someChecked =
    selectedItems.size > 0 && selectedItems.size < items.length;
  const indeterminate = someChecked;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(items.map((item) => item.id));
      setSelectedItems(allIds);
      onSelectionChange?.(Array.from(allIds));
    } else {
      setSelectedItems(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleItemChange = (itemId: string, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    setSelectedItems(newSelection);
    onSelectionChange?.(Array.from(newSelection));
  };

  // Only update state when items prop changes AND we have no current selection
  // This prevents resetting user selections
  React.useEffect(() => {
    if (selectedItems.size === 0) {
      const checkedIds = new Set(
        items.filter((item) => item.checked).map((item) => item.id)
      );
      setSelectedItems(checkedIds);
    }
  }, [items, selectedItems.size]);

  return (
    <div className={cn("wl-d-flex wl-flex-column wl-gap-8", className)}>
      {showSelectAll && (
        <div className="wl-flex wl-items-center wl-gap-2">
          <Checkbox
            id="select-all"
            checked={allChecked}
            indeterminate={indeterminate}
            onCheckedChange={handleSelectAll}
            variant={variant}
            size={size}
          />
          <label htmlFor="select-all" className="wl-cursor-pointer">
            {selectAllLabel}
          </label>
        </div>
      )}

      <div
        className={cn(
          showSelectAll && "wl-ml-6 wl-d-flex wl-flex-column wl-gap-8"
        )}
      >
        {items.map((item) => (
          <div key={item.id} className="wl-flex wl-items-center wl-gap-2">
            <Checkbox
              id={item.id}
              checked={selectedItems.has(item.id)}
              onCheckedChange={(checked) =>
                handleItemChange(item.id, checked as boolean)
              }
              disabled={item.disabled}
              variant={variant}
              size={size}
            />
            <label htmlFor={item.id} className="wl-cursor-pointer">
              {item.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export { Checkbox };
